const axios = require("axios")
const { SecretsManagerClient, GetSecretValueCommand } = require('@aws-sdk/client-secrets-manager')

const { BaseClient } = require('../shared')

class ClaimActivityClient extends BaseClient {
    constructor(claimActivityServiceUrl = process.env.CLAIM_ACTIVITY_API) {
        super(claimActivityServiceUrl)
    }

    async runClaimSyncHandler() {
        this.createAxiosInstance()

        const response = await this.axiosInstance.post(`api/batch/runClaimSyncHandler`, {})

        if (response.status !== 200) {
            return null;
        }
    
        return response.data;
    }
    
    /**
     * @param {string} memberUuid 
     * @param {json} startKey
     */
    async getClaimsPageByMember(memberUuid, startKey = null) {
        this.createAxiosInstance()
        let key = ''

        if (startKey) {
            key = `?startKey=${Buffer.from(JSON.stringify(startKey)).toString('base64')}`
        }

        const response = await this.axiosInstance.get(`query/claimsByMember/${memberUuid}${key}`)

        if (response.status !== 200) {
            return null;
        }
    
        return response.data;
    }
    
    /**
     * @param {string} memberUuid 
     */
    async getAllClaimsByMember(memberUuid) {
        let claimsList = [];
        let claimsPage = await this.getClaimsPageByMember(memberUuid);
        claimsList.push(...claimsPage.Items);
    
        while (claimsPage.LastEvaluatedKey !== "") {
            let nextPage = await this.getClaimsPageByMember(memberUuid, claimsPage.LastEvaluatedKey);
            claimsList.push(...nextPage.Items);
        }
        return claimsList;
    }
    
    /**
     * @param {string} employeeCertificateNumber 
     */
    async getClaimsPageByFamily(employeeCertificateNumber, startKey = null) {
        this.createAxiosInstance()
        let key = ''

        if (startKey) {
            key = `?startKey=${Buffer.from(JSON.stringify(startKey)).toString('base64')}`
        }

        const response = await this.axiosInstance.get(`query/claimsByFamily/${employeeCertificateNumber}${key}`)

        if (response.status !== 200) {
            return null
        }

        return response.data
    }
    
    /**
     * @param {string} employeeCertificateNumber 
     */
    async getAllClaimsByFamily(employeeCertificateNumber) {
        let claimsList = []
        let claimsPage = await this.getClaimsPageByFamily(employeeCertificateNumber)
        claimsList.push(...claimsPage.Items)
    
        while (claimsPage.LastEvaluatedKey !== "") {
            let nextPage = await this.getClaimsPageByFamily(employeeCertificateNumber, claimsPage.LastEvaluatedKey)
            claimsList.push(...nextPage.Items)
        }

        return claimsList
    }
    
    /**
     * @param {string} claimNumber 
     */
    async getClaimByClaimNumber(claimNumber) {
        this.createAxiosInstance()

        const response = await this.axiosInstance.get(`query/claimByClaimNumber/${claimNumber}`)
    
        if (response.status !== 200) {
            return null
        }
    
        return response.data;
    }
    
    /**
     * @param {string} family 
     */
    async syncFamilyClaimList(employeeCertificateNumber, memberUuid) {
        this.createAxiosInstance()

        const body = {
            subscriberFamilyIdentifier: employeeCertificateNumber,
            memberUuid: memberUuid
        };
        const response = await this.axiosInstance.post(`commands/batchSync/family`, body)
    
        if (response.status !== 200) {
            return null
        }
    
        return response
    }
}

class ClaimActivitySyncClient extends BaseClient {
    constructor(
        claimActivitySyncServiceUrl = process.env.CLAIM_ACTIVITY_SYNC_API,
        claimActivitySyncServiceSecretName = process.env.CENTIVO_CLAIM_SYNC_SECRET_NAME || 'claim-activity-key',
        axiosOptions
    ) {
        super(claimActivitySyncServiceUrl, claimActivitySyncServiceSecretName, axiosOptions)
    }

    async createSecureAxiosInstance(customHeaders = {}, contentType = null) {
        if (!this.secretName)
            throw new Error("'secretName' must be defined")

        const secretsManagerClient = new SecretsManagerClient()

        const { SecretString } = await secretsManagerClient.send(new GetSecretValueCommand({ SecretId: this.secretName }));

        this.secureAxiosInstance = axios.create({
            baseURL: this.url,
            headers: {
                "content-type": contentType ?? "application/json",
                Authorization: SecretString,
                ...customHeaders
            }
        });
    }

    /**
     * 
     * @param {string} memberUuid 
     * @param {string} source
     */
    async syncAllMemberClaims(memberUuid, source) {
        await this.createSecureAxiosInstance()

        try {
            const body = {
                memberUuid,
                source
            };
    
            const response = await this.secureAxiosInstance.post(`claims/syncAllMemberClaims`, JSON.stringify(body), {
                body
            });
    
            return response;
        }
        catch (e) {
            throw e.response;
        }
    }
    
    /**
     * 
     * @param {string} memberUuid
     */
    async getSyncStatus(memberUuid) {
        await this.createSecureAxiosInstance()

        try {
            const response = await this.secureAxiosInstance.get(`claims/getSyncStatus`, {
                params: {
                    memberUuid: memberUuid
                }
            });
    
            return response;
        }
        catch (e) {
            throw e.response;
        }
    }
}


module.exports = {
    ClaimActivityClient,
    ClaimActivitySyncClient
}